import { useState } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '../ui/dropdown-menu';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { StatusPill } from '../StatusPills';
import { Pencil, Loader2 } from 'lucide-react';

/**
 * What each AI classification means for the user, on the four status tones.
 * `mono` is the evidence-line form used in the correspondence meta line.
 */
export const CLASSIFICATION_META = {
  id_request:          { label: 'ID requested',   mono: 'ID REQUESTED',   tone: 'needs' },
  acknowledgment:      { label: 'Acknowledged',   mono: 'ACKNOWLEDGED',   tone: 'held' },
  extension_request:   { label: 'Extension',      mono: 'MORE TIME ASKED', tone: 'held' },
  data_received:       { label: 'Records sent',   mono: 'RECORDS SENT',   tone: 'done' },
  portal_link:         { label: 'Portal link',    mono: 'PORTAL LINK',    tone: 'held' },
  upload_link_request: { label: 'Upload link',    mono: 'UPLOAD LINK ASKED', tone: 'held' },
  refusal:             { label: 'Refused',        mono: 'REFUSED',        tone: 'breach' },
  clarification:       { label: 'Question for you', mono: 'QUESTION FOR YOU', tone: 'needs' },
  general:             { label: 'General',        mono: 'GENERAL',        tone: 'neutral' },
};

export const classificationMeta = (c) =>
  CLASSIFICATION_META[c] || (c ? { label: c, mono: String(c).toUpperCase(), tone: 'neutral' } : null);

const CLASSIFICATIONS = Object.entries(CLASSIFICATION_META).map(([value, m]) => ({ value, label: m.label }));

/**
 * Inline editor for an email's AI classification. After a change, the
 * backend returns `available_actions` for the new classification — these
 * are rendered as buttons (with already-done actions disabled).
 *
 * Replying / attachment actions (send_id, send_upload_link) keep their
 * dedicated existing buttons elsewhere in the UI; this control surfaces
 * the lightweight follow-ups: deadline reset, accept extension, mark complete.
 */
export const EmailClassificationEditor = ({ email, onClassificationChanged }) => {
  const [actions, setActions] = useState(null);
  const [busy, setBusy] = useState(false);
  const [extensionOpen, setExtensionOpen] = useState(false);
  const [extensionDate, setExtensionDate] = useState('');

  const meta = classificationMeta(email.ai_classification);

  const handleChange = async (newClassification) => {
    if (newClassification === email.ai_classification) return;
    setBusy(true);
    try {
      const { data } = await api.put(
        `/email/${email.id}/classification`,
        { classification: newClassification },
      );
      setActions(data.available_actions || []);
      toast.success(`Marked as “${classificationMeta(newClassification)?.label || newClassification}”.`);
      onClassificationChanged?.(newClassification);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not change that. Try again in a moment.');
    } finally {
      setBusy(false);
    }
  };

  const runAction = async (actionId, payload) => {
    setBusy(true);
    try {
      const { data } = await api.post(
        `/email/${email.id}/action/${actionId}`,
        payload || {},
      );
      toast.success(data.message || 'Done.');
      const refreshed = await api.get(`/email/${email.id}/actions`);
      setActions(refreshed.data.available_actions || []);
      onClassificationChanged?.(email.ai_classification);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'That did not work. Try again in a moment.');
    } finally {
      setBusy(false);
    }
  };

  const handleActionClick = (action) => {
    if (action.disabled || busy) return;
    if (action.id === 'accept_extension') {
      setExtensionOpen(true);
      return;
    }
    runAction(action.id);
  };

  const submitExtension = async () => {
    if (!extensionDate) return;
    setExtensionOpen(false);
    await runAction('accept_extension', { new_deadline_date: extensionDate });
    setExtensionDate('');
  };

  return (
    <>
      <span className="inline-flex items-center gap-1.5">
        {meta && (
          <StatusPill tone={meta.tone} size="sm" testId={`classification-${email.ai_classification}`}>
            {meta.label}
          </StatusPill>
        )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="inline-flex h-7 w-7 items-center justify-center rounded-md text-ink-55 transition-colors duration-state hover:text-ink hover:bg-inset disabled:opacity-50"
              disabled={busy}
              aria-label="Change what this email is"
              title="Change what this email is"
              data-testid={`reclassify-btn-${email.id}`}
            >
              <Pencil className="h-3.5 w-3.5" strokeWidth={1.5} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-56">
            <DropdownMenuLabel className="text-xs text-ink-55">What is this email?</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {CLASSIFICATIONS.map((c) => (
              <DropdownMenuItem
                key={c.value}
                onClick={() => handleChange(c.value)}
                className={c.value === email.ai_classification ? 'font-semibold text-brand' : ''}
                data-testid={`reclassify-option-${c.value}`}
              >
                {c.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </span>

      {/* Follow-ups the backend offers for the new classification */}
      {actions && actions.length > 0 && (
        <div className="mt-3 rounded-lg bg-inset p-3" data-testid="email-actions">
          <p className="pd-eyebrow mb-2">You can now</p>
          <div className="flex flex-wrap gap-2">
            {actions.map((a) => (
              <Button
                key={a.id}
                size="sm"
                variant="secondary"
                disabled={a.disabled || busy}
                onClick={() => handleActionClick(a)}
                className="bg-surface"
                data-testid={`email-action-${a.id}`}
                title={a.disabled ? a.disabled_reason : undefined}
              >
                {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                {a.label}
                {a.disabled && a.disabled_reason ? ` (${a.disabled_reason})` : ''}
              </Button>
            ))}
          </div>
        </div>
      )}

      <Dialog open={extensionOpen} onOpenChange={setExtensionOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Give them more time</DialogTitle>
            <DialogDescription>
              Organisations can ask for up to two extra months when a request is complex. The
              deadline moves to the date you choose.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="ext-date" className="text-[15px] font-semibold text-ink">
              What is the new deadline?
            </Label>
            <Input
              id="ext-date"
              type="date"
              value={extensionDate}
              onChange={(e) => setExtensionDate(e.target.value)}
              data-testid="extension-date-input"
            />
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setExtensionOpen(false)}>
              Not now
            </Button>
            <Button onClick={submitExtension} disabled={!extensionDate} data-testid="extension-submit">
              Accept extension
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default EmailClassificationEditor;
